import {
    Box,
    Grid,
    Typography,
    useTheme,
    CardHeader,
    Divider,
    Container,
} from "@mui/material";
import Link from "next/link";
import React from "react";
import {
    CoursePageProps,
    ModuleGroup,
    STARKLINGS,
    STARKLINGS1,
} from "./eduConfig";
import {
    getLessonCountFromModule,
    getModuleUrl,
    getPercentageComplete,
} from "@js/util/educationUtil";
import {
    Card,
    CardContent,
    CardActions,
    PaperHeader,
    PaperSection,
    PaperSectionTitle,
    StartModuleButton,
} from "./styled";
import { userGlobalContext } from "@js/pages/_app";

const EducationCourse = (props: CoursePageProps) => {
    const theme = useTheme();
    let course = props;
    const allCourseProgress = props.allCourseProgress;
    const { globalUser } = userGlobalContext();

    /**
     * Renders the course header
     * @returns - Jsx
     */
    const renderHeader = () => {
        let numModules = 0;
        for (let i = 0; i < course.moduleGroup.length; i++) {
            let currModGroup = course.moduleGroup[i];
            numModules += currModGroup.modules.length;
        }
        return (
            <PaperHeader sx={props.borderColorStyle}>
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Grid item md={4} lg={4} xl={4} textAlign="center">
                        <img
                            src={props.courseBannerImgLeft}
                            alt={`${props.title}-left`}
                        />
                    </Grid>
                    <Grid item md={4} lg={4} xl={4} textAlign="center">
                        <Typography variant="h3">{props.title}</Typography>
                        {props.title.toLowerCase() === STARKLINGS ||
                        props.title.toLowerCase() === STARKLINGS1 ? (
                            <Typography variant="body1" fontWeight={"light"}>
                                By OnlyDust
                            </Typography>
                        ) : (
                            <></>
                        )}
                        <Typography variant="h6">
                            {numModules} modules
                        </Typography>
                    </Grid>
                    <Grid item md={4} lg={4} xl={4} textAlign="center">
                        <img
                            src={props.courseBannerImgRight}
                            alt={`${props.title}-right`}
                        />
                    </Grid>
                </Grid>
            </PaperHeader>
        );
    };

    /**
     * @param key - unique number of the iteration we are on
     * @returns course body Jsx
     */
    function renderCourse(moduleGroup: ModuleGroup, key: number) {
        return (
            <PaperSection
                key={`moduleGroup-${key}`}
                sx={props.borderColorStyle}
            >
                <Grid container justifyContent="center" sx={{ mt: "-3rem" }}>
                    <Grid item md={8} lg={8} xl={8}>
                        <PaperSectionTitle sx={props.borderColorStyle}>
                            <Typography fontSize="1.5rem" fontWeight={700}>
                                {moduleGroup.groupName}
                            </Typography>
                        </PaperSectionTitle>
                    </Grid>
                </Grid>
                <Grid container>
                    {moduleGroup.modules.map((module: any, index: number) => {
                        let numLessons = getLessonCountFromModule(
                            course.courseName,
                            module.moduleInfo.id
                        );
                        const percentComplete = getPercentageComplete(
                            allCourseProgress,
                            course.courseName,
                            module.moduleInfo.id,
                            numLessons
                        );
                        return (
                            <Grid
                                key={`module-${index}`}
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={4}
                                xl={4}
                            >
                                {renderModuleCard(
                                    module.moduleInfo.title,
                                    numLessons,
                                    module.moduleInfo.timeEstimate!,
                                    percentComplete,
                                    module.moduleInfo.id
                                )}
                            </Grid>
                        );
                    })}
                </Grid>
            </PaperSection>
        );
    }

    /**
     * Render module card Jsx
     * @param title - module title
     * @param numLessons - number of lessons in the module
     * @param timeEstimate - time estimate of the module
     * @param percentComplete - percent user is complete
     * @param path - module id for url path
     * @returns Jsx
     */
    const renderModuleCard = (
        title: string,
        numLessons: number,
        timeEstimate: string,
        percentComplete: number,
        path: number
    ) => {
        return (
            <Box mt="5.5rem">
                <Card sx={props.borderColorStyle}>
                    {renderModuleCardHeader(title, numLessons, timeEstimate)}
                    <Divider variant="fullWidth" flexItem />
                    {renderModuleCardBody(percentComplete)}
                </Card>
                {renderModuleCardFooter(path)}
            </Box>
        );
    };

    /**
     * Renders the module card header
     * @param title - module title
     * @param numLessons - number of lessons in the module
     * @param timeEstimate - time estimate of the module
     * @returns Jsx
     */
    const renderModuleCardHeader = (
        title: string,
        numLessons: number,
        timeEstimate: string
    ) => {
        const subheader = `${numLessons} Lessons · ${timeEstimate}`;
        return (
            <CardHeader
                title={title}
                subheader={subheader}
                sx={{ textAlign: "center" }}
            />
        );
    };

    /**
     * Renders the module card body
     * @param percentComplete - percent user is complete
     * @returns Jsx
     */
    const renderModuleCardBody = (percentComplete: number) => {
        return (
            <CardContent sx={props.moduleCardBackgroundColor}>
                <Typography
                    fontSize="20px"
                    color="primary"
                    sx={props.moduleCardTextColor}
                    fontWeight={800}
                >
                    {percentComplete}% Completed
                </Typography>
            </CardContent>
        );
    };

    /**
     * Renders the module card footer
     * @param path - module id for url path
     * @returns Jsx
     */
    const renderModuleCardFooter = (path: number) => {
        const moduleUrl: string = getModuleUrl(props.courseName, path);
        return (
            <CardActions>
                <Link href={moduleUrl} passHref>
                    <StartModuleButton
                        variant="contained"
                        size="large"
                        color="primary"
                        sx={props.buttonStyle}
                    >
                        Start Module
                    </StartModuleButton>
                </Link>
            </CardActions>
        );
    };

    return (
        <Container
            maxWidth="xl"
            sx={{ p: 3, backgroundColor: theme.palette.background.paper }}
        >
            {renderHeader()}
            {course.moduleGroup.map((moduleGroup, index) =>
                renderCourse(moduleGroup, index)
            )}
        </Container>
    );
};
export default EducationCourse;
